import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import PermissionGate from "@/components/PermissionGate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BookOpen, Plus, Pencil, Trash2, Search } from "lucide-react";

const STATUSES = ["Active", "Draft", "Archived"];
const EMPTY = { category_id: "", name: "", description: "", duration_weeks: 0, course_fee: 0, status: "Active" };
const STATUS_STYLE = { Active: "bg-accent/10 text-accent", Draft: "bg-amber-100 text-amber-700", Archived: "bg-slate-100 text-slate-600" };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function CoursesTab({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/courses", { params: { search: search || undefined, category_id: catFilter } });
    setRows(data.items || []);
  }, [search, catFilter]);

  const loadCats = useCallback(async () => {
    const { data } = await api.get("/categories");
    setCats(data || []);
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { loadCats(); }, [loadCats, refreshKey]);

  const openNew = () => { setForm({ ...EMPTY, category_id: cats[0]?.id || "" }); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.category_id) { toast.error("Please select a category"); return; }
    setSaving(true);
    try {
      const payload = { ...form, duration_weeks: Number(form.duration_weeks), course_fee: Number(form.course_fee) };
      if (editId) await api.put(`/courses/${editId}`, payload);
      else await api.post("/courses", payload);
      toast.success(editId ? "Course updated" : "Course created");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete course "${c.name}"?`)) return;
    try { await api.delete(`/courses/${c.id}`); toast.success("Course deleted"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="courses-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search courses..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-64 pl-9" data-testid="course-search" />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="h-9 w-48" data-testid="course-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <PermissionGate permission="course.create">
          <Button onClick={openNew} data-testid="add-course-button"><Plus className="mr-1.5 h-4 w-4" /> Add course</Button>
        </PermissionGate>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead><TableHead>Course</TableHead><TableHead>Category</TableHead>
              <TableHead>Duration</TableHead><TableHead>Fee</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No courses yet.</TableCell></TableRow>}
            {rows.map((c) => (
              <TableRow key={c.id} data-testid={`course-row-${c.course_code}`}>
                <TableCell className="font-medium">{c.course_code}</TableCell>
                <TableCell className="flex items-center gap-2 font-medium text-secondary"><BookOpen className="h-4 w-4 text-primary" />{c.name}</TableCell>
                <TableCell>{c.category_name || "—"}</TableCell>
                <TableCell>{c.duration_weeks ? `${c.duration_weeks} wks` : "—"}</TableCell>
                <TableCell>{inr(c.course_fee)}</TableCell>
                <TableCell><Badge className={`border-0 ${STATUS_STYLE[c.status] || ""}`}>{c.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <PermissionGate permission="course.update"><Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-course-${c.course_code}`}><Pencil className="h-4 w-4" /></Button></PermissionGate>
                  <PermissionGate permission="course.delete"><Button variant="ghost" size="sm" className="text-destructive" onClick={() => remove(c)} data-testid={`delete-course-${c.course_code}`}><Trash2 className="h-4 w-4" /></Button></PermissionGate>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="course-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit course" : "Add course"}</DialogTitle>
            <DialogDescription>Define the course details, category, fee and status.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} data-testid="course-name-input" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v }))}>
                <SelectTrigger data-testid="course-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="course-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Duration (weeks)</Label><Input type="number" value={form.duration_weeks} onChange={set("duration_weeks")} data-testid="course-duration-input" /></div>
            <div><Label>Course fee (₹)</Label><Input type="number" value={form.course_fee} onChange={set("course_fee")} data-testid="course-fee-input" /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={set("description")} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-course-button">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
